import { Box, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useQuery } from "@tanstack/react-query";
import { PDF_VIEWER_MAT_BG as VIEWER_BG } from "./types.js";

import { PdfReader } from "../../components/pdf/pdf-reader.js";
import { fetchDocFileBytes } from "./api.js";

/**
 * Documentation's inline PDF preview — a thin data adapter over the shared
 * {@link PdfReader}. This component owns HOW the bytes are obtained (the
 * authenticated same-origin passthrough, or a caller-supplied `loader`); the
 * shared reader owns HOW they're rendered (pdf.js → `<canvas>`, the
 * zoom/print toolbar, the Download fallback — never a native `<iframe>` PDF
 * plugin, which hangs the macOS desktop shell).
 *
 * Bytes arrive through the authenticated SAME-ORIGIN passthrough
 * (`GET /api/documentation/documents/:id/file` → `fetchDocFileBytes`), so
 * nothing cross-origin is ever loaded; `<PdfReader>` then paints each page to a
 * `<canvas>`. On a fetch failure (or a render failure inside the reader) it
 * degrades to a Download/Open card — it can NEVER fall back to the hanging
 * iframe.
 *
 * Pinned by `PdfInlinePreview.test.tsx` + the inline-PDF case in
 * `knowledge-base.test.tsx` and `documentation.cy.ts`. See the command
 * center's documentation doc → "Inline PDF preview". The reader UI itself is
 * pinned by `components/pdf/pdf-reader.test.tsx`.
 */

export function PdfInlinePreview({
  docId,
  filename,
  loader,
  cacheKey,
}: {
  docId: number;
  filename: string;
  /** Override the byte source. Defaults to the doc's primary attachment
   * (`/file` passthrough). Used by the reader to render the `source_file`
   * ORIGINAL PDF (`/source-file`) or a PDF built client-side from the markdown
   * body (`buildDocPdf`). The returned bytes feed pdf.js — still no `<iframe>`,
   * still nothing cross-origin fetched by the browser. */
  loader?: () => Promise<{ bytes: ArrayBuffer; contentType: string | null }>;
  /** react-query key for `loader`. Must change with the source so a doc's
   * source / file / built variants don't share a cache entry. */
  cacheKey?: readonly unknown[];
}): JSX.Element {
  const { data, isLoading, isError } = useQuery({
    queryKey: cacheKey ?? ["docs", "file-bytes", docId],
    queryFn: () => (loader ? loader() : fetchDocFileBytes(docId)),
    staleTime: 5 * 60 * 1000,
  });

  // Shared Download/Open fallback copy — used both when the byte fetch fails
  // (below) and, inside `<PdfReader>`, when pdf.js can't render the bytes. Same
  // wording + `pdf-inline-fallback` test id in both cases, so the user sees one
  // consistent card no matter which layer failed.
  const fallback = (
    <>
      This PDF couldn’t be previewed inline. Use{" "}
      <Box component="span" sx={{ fontWeight: 500, color: "text.primary" }}>
        Download
      </Box>{" "}
      above to save {filename}.
    </>
  );

  if (isLoading) {
    return (
      <Box
        data-testid="pdf-inline-loading"
        sx={{
          display: "grid",
          placeItems: "center",
          height: "70vh",
          borderRadius: 1,
          border: 1,
          borderColor: "divider",
          bgcolor: VIEWER_BG,
        }}
      >
        <Typography sx={{ fontSize: 13, color: alpha("#fff", 0.6) }}>Loading PDF…</Typography>
      </Box>
    );
  }

  // Byte fetch failed (or returned nothing) — the reader never mounts, so show
  // the Download card here. A render-time failure once bytes DO load is handled
  // inside `<PdfReader>` (same test id + copy).
  if (isError || !data) {
    return (
      <Box
        data-testid="pdf-inline-fallback"
        sx={{
          borderRadius: 1,
          border: 1,
          borderColor: "divider",
          bgcolor: (t) => alpha(t.palette.surfaceSoft, 0.3),
          p: 2,
          fontSize: 14,
          color: "text.disabled",
        }}
      >
        {fallback}
      </Box>
    );
  }

  return (
    <PdfReader
      bytes={data.bytes}
      filename={filename}
      containerTestId="pdf-inline-preview"
      toolbarTestId="pdf-inline-toolbar"
      pagesTestId="pdf-inline-pages"
      fallbackTestId="pdf-inline-fallback"
      fallbackText={fallback}
    />
  );
}
